import { createContext, useContext, useState, type ReactNode } from "react";
import { toast } from "react-hot-toast";

import type { AgentStoredDocument } from "../api/e2e";

export type AgentDocumentOpenAction = (
  document: AgentStoredDocument,
) => void | Promise<void>;

const AgentDocumentActionContext =
  createContext<AgentDocumentOpenAction | null>(null);

export function AgentDocumentActionProvider({
  onOpenDocument,
  children,
}: {
  onOpenDocument: AgentDocumentOpenAction;
  children: ReactNode;
}) {
  return (
    <AgentDocumentActionContext.Provider value={onOpenDocument}>
      {children}
    </AgentDocumentActionContext.Provider>
  );
}

export function useAgentDocumentOpenAction(): AgentDocumentOpenAction | null {
  return useContext(AgentDocumentActionContext);
}

export function useAgentDocumentOpener() {
  const hostOpenDocument = useAgentDocumentOpenAction();
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(
    null,
  );
  const [previewDocument, setPreviewDocument] =
    useState<AgentStoredDocument | null>(null);

  const openDocument = (document: AgentStoredDocument) => {
    if (!hostOpenDocument) {
      setPreviewDocument(document);
      return;
    }
    setOpeningDocumentId(document.id);
    void Promise.resolve(hostOpenDocument(document))
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error
            ? error.message
            : "The document could not be opened.",
        ),
      )
      .finally(() => setOpeningDocumentId(null));
  };

  return {
    closePreview: () => setPreviewDocument(null),
    openDocument,
    openingDocumentId,
    previewDocument,
  };
}
